import { generateId } from '@/services/idService'
import { routeSegment } from '@/services/routeEditService'
import { spinningRoutePoint } from '@/services/routingService'
import { useUiStore } from '@/stores/uiStore'
import type { Segment } from '@/types/tour'

import type { Ref } from 'vue'

/**
 * Handlers for delete / drag of "pending" route-point markers emitted by
 * `RoutePointLayer` while a track edit session is active. The rebuilding logic
 * still operates on both drawing-mode and edit-mode segment state so both hosts
 * stay in sync.
 *
 * The pending anchors live implicitly in the segment arrays:
 * - **Drawing mode** (`isTrackDrawingActive`): `drawingTrackSegments` where
 *   `segments[0]` is the single-point seed at the first click and
 *   `segments[i]` (i >= 1) is the route from anchor[i-1] → anchor[i].
 * - **Edit mode append 'end'**: `editModeNewSegments` where each segment is
 *   a route from the previous anchor (existing track end for segment 0) to
 *   the next pending click.
 * - **Edit mode append 'start'**: segments are unshifted so `segments[0]`
 *   connects the newest click to the next-older click, and the last segment
 *   ends at the existing track's first anchor.
 *
 * For any pending-RP edit we take the current anchor list, apply the edit,
 * and rebuild the whole segment array by re-routing adjacent anchors.
 */
export interface PendingRoutePointOpsDeps {
  drawingTrackSegments: Ref<Segment[]>
  drawingTrackPoints: Ref<[number, number][]>
  lastClickedPoint: Ref<[number, number] | null>
  editModeNewSegments: Ref<Segment[]>
  isCalculatingRoute: Ref<boolean>
  /** Called on successful edit – used to flip the Save-button's dirty flag in edit mode. */
  onEdited?: () => void
  /** Called with a user-facing error message when routing fails. */
  onError: (message: string) => void
}

function pendingAnchorsFromDrawing(
  drawingTrackSegments: Segment[],
): { lat: number; lon: number }[] {
  const anchors: { lat: number; lon: number }[] = []
  for (const seg of drawingTrackSegments) {
    if (seg.points.length === 0) continue
    const last = seg.points[seg.points.length - 1]
    anchors.push({ lat: last.lat, lon: last.lon })
  }
  return anchors
}

function pendingAnchorsFromEdit(
  editModeNewSegments: Segment[],
  direction: 'start' | 'end',
): { lat: number; lon: number }[] {
  const anchors: { lat: number; lon: number }[] = []
  for (const seg of editModeNewSegments) {
    if (seg.points.length === 0) continue
    const pt = direction === 'end' ? seg.points[seg.points.length - 1] : seg.points[0]
    anchors.push({ lat: pt.lat, lon: pt.lon })
  }
  return anchors
}

function editSeed(
  editModeNewSegments: Segment[],
  direction: 'start' | 'end',
): { lat: number; lon: number } | null {
  if (editModeNewSegments.length === 0) return null
  if (direction === 'end') {
    const p = editModeNewSegments[0].points[0]
    return p ? { lat: p.lat, lon: p.lon } : null
  }
  const last = editModeNewSegments[editModeNewSegments.length - 1]
  const p = last.points[last.points.length - 1]
  return p ? { lat: p.lat, lon: p.lon } : null
}

export function usePendingRoutePointOps(deps: PendingRoutePointOpsDeps) {
  const uiStore = useUiStore()

  const rebuildDrawing = async (anchors: { lat: number; lon: number }[]): Promise<boolean> => {
    if (anchors.length === 0) {
      deps.drawingTrackSegments.value = []
      deps.drawingTrackPoints.value = []
      deps.lastClickedPoint.value = null
      return true
    }

    const first = anchors[0]
    const newSegments: Segment[] = [
      {
        clientId: generateId(),
        points: [{ clientId: generateId(), lat: first.lat, lon: first.lon }],
      },
    ]

    for (let i = 1; i < anchors.length; i++) {
      const seg = await routeSegment(anchors[i - 1], anchors[i], uiStore.routingProfile)
      if (!seg) return false
      newSegments.push(seg)
    }

    const points: [number, number][] = []
    newSegments.forEach((seg) => seg.points.forEach((p) => points.push([p.lon, p.lat])))

    deps.drawingTrackSegments.value = newSegments
    deps.drawingTrackPoints.value = points
    const lastAnchor = anchors[anchors.length - 1]
    deps.lastClickedPoint.value = [lastAnchor.lat, lastAnchor.lon]
    return true
  }

  const rebuildEdit = async (
    pending: { lat: number; lon: number }[],
    direction: 'start' | 'end',
    seed: { lat: number; lon: number },
  ): Promise<boolean> => {
    if (pending.length === 0) {
      deps.editModeNewSegments.value = []
      deps.drawingTrackSegments.value = []
      deps.drawingTrackPoints.value = []
      deps.lastClickedPoint.value = [seed.lat, seed.lon]
      return true
    }

    const anchorList = direction === 'end' ? [seed, ...pending] : [...pending, seed]

    const newSegments: Segment[] = []
    for (let i = 0; i < anchorList.length - 1; i++) {
      const seg = await routeSegment(anchorList[i], anchorList[i + 1], uiStore.routingProfile)
      if (!seg) return false
      newSegments.push(seg)
    }

    const points: [number, number][] = []
    newSegments.forEach((seg) => seg.points.forEach((p) => points.push([p.lon, p.lat])))

    deps.editModeNewSegments.value = newSegments
    deps.drawingTrackSegments.value = [...newSegments]
    deps.drawingTrackPoints.value = points

    const lastAnchor = pending[0]
    if (direction === 'end') {
      const latest = pending[pending.length - 1]
      deps.lastClickedPoint.value = [latest.lat, latest.lon]
    } else {
      deps.lastClickedPoint.value = [lastAnchor.lat, lastAnchor.lon]
    }
    return true
  }

  const applyEdit = async (
    transform: (anchors: { lat: number; lon: number }[]) => { lat: number; lon: number }[],
  ): Promise<boolean> => {
    if (deps.isCalculatingRoute.value) return false

    try {
      deps.isCalculatingRoute.value = true

      if (uiStore.isTrackEditMode) {
        const direction = uiStore.editingTrackAppendDirection
        const seed = editSeed(deps.editModeNewSegments.value, direction)
        if (!seed) return false
        const pending = pendingAnchorsFromEdit(deps.editModeNewSegments.value, direction)
        const newPending = transform(pending)
        const ok = await rebuildEdit(newPending, direction, seed)
        if (!ok) {
          deps.onError('Routing failed – could not update route point')
          return false
        }
        deps.onEdited?.()
        return true
      }

      if (uiStore.isTrackDrawingActive) {
        const anchors = pendingAnchorsFromDrawing(deps.drawingTrackSegments.value)
        const newAnchors = transform(anchors)
        const ok = await rebuildDrawing(newAnchors)
        if (!ok) {
          deps.onError('Routing failed – could not update route point')
          return false
        }
        return true
      }

      return false
    } finally {
      deps.isCalculatingRoute.value = false
      spinningRoutePoint.value = null
    }
  }

  const handlePendingDeleted = async (pendingIndex: number) => {
    await applyEdit((anchors) => {
      if (pendingIndex < 0 || pendingIndex >= anchors.length) return anchors
      const next = [...anchors]
      next.splice(pendingIndex, 1)
      return next
    })
  }

  const handlePendingDragend = async (pendingIndex: number, newLat: number, newLon: number) => {
    spinningRoutePoint.value = { lat: newLat, lon: newLon }
    await applyEdit((anchors) => {
      if (pendingIndex < 0 || pendingIndex >= anchors.length) return anchors
      const next = [...anchors]
      next[pendingIndex] = { lat: newLat, lon: newLon }
      return next
    })
  }

  return { handlePendingDeleted, handlePendingDragend }
}
